import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  updatePatientProfile,
  updateShippingInfo,
  getMedicationReminders,
  setMedicationReminders,
  type PatientProfileApiDto,
} from "@/data/requests/patientRequests"
import type { SelfApiDto } from "@/model/Self"
import type { ShippingAddressApiDto } from "@/model/ShippingAddress"

const selfQueryKey = ["self"]
const remindersQueryKey = (patientId: string) => ["medication-reminders", patientId]

export const useUpdatePatientProfileMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: updatePatientProfile,
    onMutate: async ({ profile }) => {
      await queryClient.cancelQueries({ queryKey: selfQueryKey })
      const previous = queryClient.getQueryData<SelfApiDto>(selfQueryKey)
      if (previous) {
        queryClient.setQueryData<SelfApiDto>(selfQueryKey, mergeProfileIntoSelf(previous, profile))
      }
      return () => {
        if (previous) queryClient.setQueryData(selfQueryKey, previous)
      }
    },
    onError: (_err, _variables, rollback) => {
      if (rollback) rollback()
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: selfQueryKey })
    },
  })
}

export const useUpdateShippingInfoMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: updateShippingInfo,
    onMutate: async ({ address }) => {
      await queryClient.cancelQueries({ queryKey: selfQueryKey })
      const previous = queryClient.getQueryData<SelfApiDto>(selfQueryKey)
      if (previous) {
        queryClient.setQueryData<SelfApiDto>(selfQueryKey, { ...previous, address })
      }
      return () => {
        if (previous) queryClient.setQueryData(selfQueryKey, previous)
      }
    },
    onError: (_err, _variables, rollback) => {
      if (rollback) rollback()
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: selfQueryKey })
    },
  })
}

export const useGetMedicationRemindersQuery = ({ patientId }: { patientId: string }) =>
  useQuery({
    queryKey: remindersQueryKey(patientId),
    queryFn: () => getMedicationReminders({ patientId }),
    enabled: patientId !== "",
    staleTime: 60_000,
  })

export const useSetMedicationRemindersMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: setMedicationReminders,
    onMutate: async ({ patientId, reminderTimes }) => {
      const queryKey = remindersQueryKey(patientId)
      await queryClient.cancelQueries({ queryKey })
      const previous = queryClient.getQueryData<string[]>(queryKey)
      queryClient.setQueryData<string[]>(queryKey, reminderTimes)
      return () => {
        queryClient.setQueryData<string[]>(queryKey, previous)
      }
    },
    onError: (_err, _variables, rollback) => {
      if (rollback) rollback()
    },
    onSettled: (_data, _err, variables) => {
      queryClient.invalidateQueries({ queryKey: remindersQueryKey(variables.patientId) })
    },
  })
}

// Profile updates can include a partial PatientAppState; merge it into the
// existing self snapshot rather than overwriting (PATCH semantics).
const mergeProfileIntoSelf = (
  self: SelfApiDto,
  profile: PatientProfileApiDto,
): SelfApiDto => ({
  ...self,
  heightInCentimeters: profile.heightInCentimeters ?? self.heightInCentimeters,
  weightInKilograms: profile.weightInKilograms ?? self.weightInKilograms,
  numberOfFetuses: profile.numberOfFetuses ?? self.numberOfFetuses,
  estimatedDateOfDelivery: profile.estimatedDateOfDelivery ?? self.estimatedDateOfDelivery,
  medications: profile.medications ?? self.medications,
  patientAppState: profile.patientAppState
    ? { ...self.patientAppState, ...profile.patientAppState }
    : self.patientAppState,
})

// Re-export so consumers can still import the types from here without reaching
// across modules.
export type { PatientProfileApiDto, ShippingAddressApiDto }
